// Vendored from the JETCAM DXF Viewer (src/render/colors.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type LayerRecord } from "../parse/index.ts";

/**
 * The AutoCAD Color Index (ACI).
 *
 * Indices 0 and 256 are not colours at all: 0 is BYBLOCK and 256 is BYLAYER.
 * BYBLOCK is already resolved by `geometry/`, which is the only layer that
 * knows the INSERT stack; BYLAYER is resolved here, because it needs the LAYER
 * table and the background.
 */

export const BYBLOCK = 0;
export const BYLAYER = 256;

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** Indices 1-9, which is what nearly every real CAM file actually uses. */
const PRIMARY: Readonly<Record<number, Rgb>> = {
  1: { r: 255, g: 0, b: 0 },
  2: { r: 255, g: 255, b: 0 },
  3: { r: 0, g: 255, b: 0 },
  4: { r: 0, g: 255, b: 255 },
  5: { r: 0, g: 0, b: 255 },
  6: { r: 255, g: 0, b: 255 },
  // 7 is deliberately absent: it is the "same as the background's opposite"
  // colour and is resolved against the current theme, not from this table.
  8: { r: 65, g: 65, b: 65 },
  9: { r: 128, g: 128, b: 128 },
};

/** Indices 250-255, the grey ramp. */
const GREYS: Readonly<Record<number, Rgb>> = {
  250: { r: 51, g: 51, b: 51 },
  251: { r: 80, g: 80, b: 80 },
  252: { r: 105, g: 105, b: 105 },
  253: { r: 130, g: 130, b: 130 },
  254: { r: 174, g: 174, b: 174 },
  255: { r: 255, g: 255, b: 255 },
};

/**
 * Value levels for the chromatic band, indices 10-249.
 *
 * That band is 24 hues at 15 degree steps, each with ten variants: five
 * brightness levels, each at full and then half saturation. These are the
 * levels AutoCAD uses — 165 rather than a tidy 191 for the second step, for
 * instance — so the generated table matches the canonical one rather than
 * merely resembling it.
 */
const VALUE_LEVELS = [255, 165, 127, 76, 38];

function hsvToRgb(hueDegrees: number, saturation: number, value: number): Rgb {
  const h = (((hueDegrees % 360) + 360) % 360) / 60;
  const c = value * saturation;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = value - c;

  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 1) [r, g, b] = [c, x, 0];
  else if (h < 2) [r, g, b] = [x, c, 0];
  else if (h < 3) [r, g, b] = [0, c, x];
  else if (h < 4) [r, g, b] = [0, x, c];
  else if (h < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  // Truncate rather than round: AutoCAD's own table does, and the difference
  // shows at exactly the half-saturation steps (index 11 is 255,127,127 —
  // rounding gives 128 and quietly stops matching the canonical palette).
  return { r: Math.floor(r + m), g: Math.floor(g + m), b: Math.floor(b + m) };
}

/** The full 0-255 table, built once. */
const ACI: (Rgb | null)[] = (() => {
  const table: (Rgb | null)[] = new Array<Rgb | null>(256).fill(null);
  for (const [index, rgb] of Object.entries(PRIMARY)) table[Number(index)] = rgb;
  for (const [index, rgb] of Object.entries(GREYS)) table[Number(index)] = rgb;

  for (let i = 10; i <= 249; i += 1) {
    const n = i - 10;
    const hue = Math.floor(n / 10) * 15;
    const variant = n % 10;
    const value = VALUE_LEVELS[Math.floor(variant / 2)] ?? 255;
    // Even variants are fully saturated; odd ones are the washed-out pair.
    const saturation = variant % 2 === 0 ? 1 : 0.5;
    table[i] = hsvToRgb(hue, saturation, value);
  }
  return table;
})();

export function rgbToCss(c: Rgb): string {
  return `rgb(${c.r} ${c.g} ${c.b})`;
}

/** Perceived brightness, 0-255, used to decide what index 7 should be. */
export function luminance(c: Rgb): number {
  return 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
}

export interface ColorContext {
  /** Canvas background, so index 7 can be resolved against it. */
  readonly background: Rgb;
  readonly layers: ReadonlyMap<string, LayerRecord>;
}

/**
 * Colour index 7.
 *
 * In AutoCAD this is "white or black, whichever contrasts with the drawing
 * area". Mapping it to literal white is the classic viewer bug: every file
 * whose geometry sits on colour 7 — which is the default — disappears on a
 * light background. It is resolved against the actual background instead.
 */
export function contrastColor(background: Rgb): Rgb {
  return luminance(background) > 127 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
}

/** A 24-bit true colour value from group 420. */
export function trueColorToRgb(value: number): Rgb {
  return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

/** Look up a plain colour index, with index 7 and unknown values handled. */
export function aciToRgb(index: number, background: Rgb): Rgb {
  if (index === 7) return contrastColor(background);
  const found = ACI[index];
  if (found) return found;
  // Out of range, or one of the two pseudo-indices reaching here by mistake.
  return contrastColor(background);
}

/**
 * Resolve the colour a primitive should actually be drawn in.
 *
 * True colour wins outright when present. Otherwise BYLAYER walks to the
 * layer's own colour — which may itself be 7 — and anything else is a direct
 * index. A layer named in an entity but missing from the table is not an error
 * worth failing over; it falls back to the contrast colour.
 */
export function resolveColor(
  color: number,
  layerName: string,
  ctx: ColorContext,
  trueColor?: number,
): Rgb {
  if (trueColor !== undefined) return trueColorToRgb(trueColor);

  if (color === BYLAYER || color === BYBLOCK) {
    const layer = ctx.layers.get(layerName);
    if (!layer) return contrastColor(ctx.background);
    if (layer.trueColor !== undefined) return trueColorToRgb(layer.trueColor);
    return aciToRgb(layer.color, ctx.background);
  }

  return aciToRgb(color, ctx.background);
}

/**
 * The colour index a primitive actually carries, for display purposes — as
 * opposed to `resolveColor`, which turns it into a pixel.
 *
 * Two ways there can be no index to show, told apart because they call for
 * different messages: `trueColor` when a 24-bit colour wins instead (on the
 * primitive itself, or on the layer when BYLAYER), and `unresolved` when
 * BYLAYER points at a layer the LAYER table does not declare (the same
 * "undeclared" case the Layers tab already flags).
 */
export type ColorIndexResult =
  | { readonly kind: "index"; readonly index: number }
  | { readonly kind: "trueColor" }
  | { readonly kind: "unresolved" };

export function resolveColorIndex(
  color: number,
  layerName: string,
  layers: ReadonlyMap<string, LayerRecord>,
  trueColor?: number,
): ColorIndexResult {
  if (trueColor !== undefined) return { kind: "trueColor" };
  if (color !== BYLAYER) return { kind: "index", index: color };
  const layer = layers.get(layerName);
  if (!layer) return { kind: "unresolved" };
  if (layer.trueColor !== undefined) return { kind: "trueColor" };
  return { kind: "index", index: layer.color };
}

/**
 * The linetype name a primitive actually draws with. `BYLAYER` (the default
 * when an entity does not override it — see `PrimitiveBase.linetype`) walks
 * to the layer's own linetype; a layer named in an entity but missing from
 * the table, or one that never declares a linetype, falls back to
 * `CONTINUOUS` rather than showing `BYLAYER` itself, which is not a linetype
 * a reseller would recognise on the machine.
 */
export function resolveLinetype(
  linetype: string,
  layerName: string,
  layers: ReadonlyMap<string, LayerRecord>,
): string {
  if (linetype !== "BYLAYER") return linetype;
  return layers.get(layerName)?.linetype ?? "CONTINUOUS";
}
